import type { PdfSectionWithPages } from "@repo/shared/types";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/page-header";
import { DocButton } from "@/components/ui/doc-button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UnderlineTab, UnderlineTabs } from "@/components/ui/underline-tabs";
import { useTabParam } from "@/hooks/use-tab-param";
import { useAllCustomFields } from "@/lib/hooks/use-clients";
import { usePdfTemplateConfiguredAssessmentTypes } from "@/lib/hooks/use-configured-assessment-types";
import { useFeatureFlag } from "@/lib/hooks/use-feature-flags";
import {
  usePdfAttachments,
  usePdfFlowFields,
  usePdfSectionPages,
  usePdfSections,
  usePdfTemplate,
} from "@/lib/hooks/use-pdf-templates";
import { usePortfolioCollectionSettings } from "@/lib/hooks/use-portfolio-collections";
import { usePdfTemplateQuizzes } from "@/lib/hooks/use-quizzes";
import { useT } from "@/lib/i18n/use-t";
import { PdfEditor } from "./pdf-editor";
import { PdfTemplateAttachments } from "./pdf-template-attachments";
import { PdfTemplateDetailLoader } from "./pdf-template-detail-loader";
import { PdfTemplateSettings } from "./pdf-template-settings";
import { PdfTemplateStyle } from "./pdf-template-style";
import { T } from "./pdf-template-tabs.i18n";
import { PreviewButton } from "./preview-button";
import { SectionDialog } from "./section-dialog";
import { SectionList } from "./section-list";
import type { PdfTemplatePage } from "./types";

const TAB_VALUES = ["settings", "style", "sections", "attachments"] as const;

type PdfTemplateTabsProps = {
  templateId: string;
};

export function PdfTemplateTabs({ templateId }: PdfTemplateTabsProps) {
  const t = useT(T);
  const [activeTab, setActiveTab] = useTabParam(TAB_VALUES, "settings");

  const { template, isLoading: templateLoading } = usePdfTemplate(templateId);
  const { sections, isLoading: sectionsLoading } = usePdfSections(templateId);
  const { fields: flowFields } = usePdfFlowFields(templateId);
  // QL-13 — the `quiz` components on this template's flow.
  const { quizzes, isUnknown: quizzesUnknown } = usePdfTemplateQuizzes(templateId);
  const { configuredAssessmentTypes, isUnknown: assessmentTypesUnknown } =
    usePdfTemplateConfiguredAssessmentTypes(templateId);
  const { attachments } = usePdfAttachments(templateId);

  const [selectedSection, setSelectedSection] = useState<PdfSectionWithPages | null>(null);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [sectionSettingsOpen, setSectionSettingsOpen] = useState(false);
  const { enabled: hasXRayAccess } = useFeatureFlag("morningstar_xray");
  const { customFields: allCustomFields } = useAllCustomFields();
  // Standard pseudo-fields are not custom_field rows — a
  // "__custom_field_standard-*" rule source could never resolve at runtime;
  // their data exists as dedicated sentinels (Client Country, Client Type, …).
  const clientCustomFields = useMemo(
    () => allCustomFields.filter((f: { isStandard?: boolean }) => !f.isStandard),
    [allCustomFields],
  );

  // QP-208 — a code-template section can carry several pages (e.g. "Wealth
  // Plan" + "Costs"); load them all and let the user switch between them.
  const { pages: sectionPages, isLoading: loadingPage } = usePdfSectionPages(
    selectedSection?.id ?? null,
  );
  const sectionPage =
    sectionPages.find((p: { id: string }) => p.id === selectedPageId) ?? sectionPages[0] ?? null;

  const { settings: collectionSettings } = usePortfolioCollectionSettings(
    template?.flow?.portfolioCollectionId ?? null,
  );

  const isMultiGoalFlow = template?.flow?.multiGoal ?? false;

  // Sync selectedSection with sections when sections data changes
  useEffect(() => {
    if (selectedSection) {
      const updatedSection = sections.find((s: any) => s.id === selectedSection.id);
      if (updatedSection && updatedSection !== selectedSection) {
        setSelectedSection(updatedSection);
      }
    }
  }, [sections, selectedSection]);

  const handleSelectSection = (section: PdfSectionWithPages) => {
    setSelectedSection(section);
    setSelectedPageId(null);
  };

  const handleBackToSections = () => {
    setSelectedSection(null);
    setSelectedPageId(null);
  };

  if (templateLoading || sectionsLoading) {
    return <PdfTemplateDetailLoader />;
  }

  if (!template) {
    return <div className="text-center py-24 text-muted-foreground">{t.templateNotFound}</div>;
  }

  // Build breadcrumb items based on current state
  const breadcrumbItems = selectedSection
    ? [
        { label: t.settings, href: "/settings" },
        { label: t.pdfTemplates, href: "/settings/pdf-templates" },
        { label: template.name, onClick: handleBackToSections },
        { label: selectedSection.name },
      ]
    : [
        { label: t.settings, href: "/settings" },
        { label: t.pdfTemplates, href: "/settings/pdf-templates" },
        { label: template.name },
      ];

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={breadcrumbItems}
        title={template.name}
        description={
          <>
            {t.pdfTemplateFor} {template.flow.name}
          </>
        }
        actions={
          <>
            <PreviewButton templateId={template.id} flowId={template.flowId} />
            <DocButton docSlug="creating-templates" variant="outline" size="sm" className="gap-2">
              {t.documentation}
            </DocButton>
          </>
        }
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="settings">{t.settingsTab}</TabsTrigger>
          <TabsTrigger value="style">{t.styleTab}</TabsTrigger>
          <TabsTrigger value="sections">
            {t.sectionsTab}
            {sections.length > 0 && (
              <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                {sections.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="attachments">
            {t.attachmentsTab}
            {attachments.length > 0 && (
              <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                {attachments.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="space-y-4">
          <PdfTemplateSettings template={template} />
        </TabsContent>

        <TabsContent value="style" className="space-y-4">
          <PdfTemplateStyle template={template} />
        </TabsContent>

        <TabsContent value="attachments" className="space-y-4">
          <PdfTemplateAttachments
            templateId={template.id}
            flowFields={flowFields}
            quizzes={quizzes}
            quizzesUnknown={quizzesUnknown}
            configuredAssessmentTypes={configuredAssessmentTypes}
            assessmentTypesUnknown={assessmentTypesUnknown}
            clientCustomFields={clientCustomFields}
          />
        </TabsContent>

        <TabsContent value="sections" className="space-y-4">
          {selectedSection ? (
            <div className="space-y-4">
              {/* Section editor (back arrow + name + scope + actions render in the toolbar) */}
              {loadingPage ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : sectionPage ? (
                <>
                  {sectionPages.length > 1 && (
                    // Second level, so underline and not pills. The section row
                    // above is already pills; repeating that design one level
                    // down makes the two rows read as siblings and hides which
                    // one is nested in which.
                    <UnderlineTabs>
                      {sectionPages.map((p: { id: string; name: string }) => (
                        <UnderlineTab
                          key={p.id}
                          label={p.name}
                          active={p.id === sectionPage.id}
                          onClick={() => setSelectedPageId(p.id)}
                        />
                      ))}
                    </UnderlineTabs>
                  )}
                  <PdfEditor
                    key={sectionPage.id}
                    templateId={template.id}
                    flowId={template.flowId}
                    initialPage={sectionPage as unknown as PdfTemplatePage}
                    flowFields={flowFields}
                    quizzes={quizzes}
                    quizzesUnknown={quizzesUnknown}
                    configuredAssessmentTypes={configuredAssessmentTypes}
                    assessmentTypesUnknown={assessmentTypesUnknown}
                    hasXRayAccess={hasXRayAccess}
                    hasRiskModel={!!template.flow.riskModelId}
                    riskModelId={template.flow.riskModelId ?? null}
                    portfolioCollectionId={template.flow.portfolioCollectionId ?? null}
                    templateLanguage={template.language}
                    collectionSettings={collectionSettings}
                    sectionId={selectedSection.id}
                    sectionScope={selectedSection.scope as "global" | "per_goal" | "per_portfolio"}
                    sectionName={selectedSection.name}
                    onBack={handleBackToSections}
                    clientCustomFields={clientCustomFields}
                    onSectionSettings={() => setSectionSettingsOpen(true)}
                  />
                </>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  {t.failedToLoadSection}
                </div>
              )}
            </div>
          ) : (
            <SectionList
              templateId={template.id}
              sections={sections}
              isMultiGoalFlow={isMultiGoalFlow}
              onSelectSection={handleSelectSection}
              selectedSectionId={(selectedSection as PdfSectionWithPages | null)?.id ?? null}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Section Settings Dialog */}
      {selectedSection && (
        <SectionDialog
          open={sectionSettingsOpen}
          onOpenChange={setSectionSettingsOpen}
          templateId={template.id}
          section={selectedSection}
          isMultiGoalFlow={isMultiGoalFlow}
          clientCustomFields={clientCustomFields}
        />
      )}
    </div>
  );
}
